import { create } from "zustand";
import { toast } from "sonner";
import $api from "@/zapi-frontend/shared/api/axios";
import { TClaimsGenerationStatus } from "./types";
import { getStatusAll } from "@/zapi-frontend/widgets/ChatIsland";
import { EGenerationStatus, EGenerationTaskType } from "@/zapi-frontend/widgets/ChatIsland/types";
import { postGenerateClaims } from "./services/postGenerateClaims";
import { postCancelGeneration } from "./services/postCancelGeneration";
import { useEffect } from "react";

type TClaimsGenerationState = {
  isGenerating: boolean;
  isCancelling: boolean;
  generationId: string | null;
  progressMessage: string;
};

type TClaimsStore = {
  claimsGenerationStates: Record<string, TClaimsGenerationState>;
  pollingTimeouts: Record<string, NodeJS.Timeout>;
  initializedChapters: Set<string>;

  setClaimsGenerationState: (chapterId: string, updates: Partial<TClaimsGenerationState>) => void;
  clearClaimsGenerationState: (chapterId: string) => void;

  startGeneration: (projectId: number, chapterId: number, onComplete: () => Promise<void>) => Promise<void>;
  startPolling: (generationId: string, chapterId: string, onComplete: () => Promise<void>) => Promise<void>;
  stopPolling: (chapterId: string) => void;
  clearAllPolling: () => void;
  cancelGeneration: (chapterId: string) => Promise<void>;

  initializePolling: (projectId: string, chapterId: string, onComplete: () => Promise<void>) => Promise<void>;
};

const useClaimsStoreInternal = create<TClaimsStore>((set, get) => ({
  claimsGenerationStates: {},
  pollingTimeouts: {},
  initializedChapters: new Set(),

  initializePolling: async (projectId, chapterId, onComplete) => {
    const { initializedChapters, claimsGenerationStates, setClaimsGenerationState, startPolling } = get();

    if (initializedChapters.has(chapterId)) return;

    const isInitialized = claimsGenerationStates[chapterId] !== undefined;
    if (isInitialized) return;

    try {
      const statusAllData = await getStatusAll(projectId);
      const task = statusAllData.find(
        (task) =>
          task.chapterId === Number(chapterId) &&
          task.type === EGenerationTaskType.claims &&
          (task.status === EGenerationStatus.pending || task.status === EGenerationStatus.processing)
      );

      if (task) {
        await startPolling(task.id.toString(), chapterId, onComplete);
      } else {
        setClaimsGenerationState(chapterId, {
          isGenerating: false,
          isCancelling: false,
          generationId: null,
          progressMessage: ""
        });
      }

      set((state) => ({
        initializedChapters: new Set([...state.initializedChapters, chapterId])
      }));
    } catch (error) {
      console.error("Failed to initialize polling:", error);
      setClaimsGenerationState(chapterId, {
        isGenerating: false,
        isCancelling: false,
        generationId: null,
        progressMessage: ""
      });
    }
  },

  setClaimsGenerationState: (chapterId, updates) => {
    set((state) => ({
      claimsGenerationStates: {
        ...state.claimsGenerationStates,
        [chapterId]: {
          ...(state.claimsGenerationStates[chapterId] || {
            isGenerating: false,
            isCancelling: false,
            generationId: null,
            progressMessage: ""
          }),
          ...updates
        }
      }
    }));
  },

  clearClaimsGenerationState: (chapterId) => {
    set((state) => {
      const newStates = { ...state.claimsGenerationStates };
      delete newStates[chapterId];
      return { claimsGenerationStates: newStates };
    });
  },

  startGeneration: async (projectId, chapterId, onComplete) => {
    const { setClaimsGenerationState, clearClaimsGenerationState, startPolling } = get();
    const chapterIdStr = chapterId.toString();

    setClaimsGenerationState(chapterIdStr, {
      isGenerating: true,
      generationId: null,
      progressMessage: "Starting claims analysis..."
    });

    try {
      const response = await postGenerateClaims({ projectId, chapterId });

      setClaimsGenerationState(chapterIdStr, {
        isGenerating: true,
        generationId: response.generationId.toString(),
        progressMessage: "Claims analysis queued..."
      });

      startPolling(response.generationId.toString(), chapterIdStr, onComplete);
    } catch (error) {
      toast.error("Failed to start claims generation");
      clearClaimsGenerationState(chapterIdStr);
      throw error;
    }
  },

  startPolling: async (generationId, chapterId, onComplete) => {
    const { pollingTimeouts, setClaimsGenerationState, clearClaimsGenerationState, stopPolling } = get();

    if (pollingTimeouts[chapterId]) {
      clearTimeout(pollingTimeouts[chapterId]);
    }

    const poll = async () => {
      try {
        const response = await $api.get<TClaimsGenerationStatus>(`/generation-tasks/${generationId}/status`);
        const status = response.data.status;

        if (status === "pending") {
          setClaimsGenerationState(chapterId, {
            isGenerating: true,
            generationId,
            progressMessage: "Claims analysis queued..."
          });
          const timeout = setTimeout(poll, 5000);
          set((state) => ({
            pollingTimeouts: { ...state.pollingTimeouts, [chapterId]: timeout }
          }));
        } else if (status === "processing") {
          setClaimsGenerationState(chapterId, {
            isGenerating: true,
            generationId,
            progressMessage: "Processing claims..."
          });
          const timeout = setTimeout(poll, 5000);
          set((state) => ({
            pollingTimeouts: { ...state.pollingTimeouts, [chapterId]: timeout }
          }));
        } else if (status === "ready") {
          await onComplete();
          clearClaimsGenerationState(chapterId);
          stopPolling(chapterId);
          toast.success("Claims extracted successfully!");
        } else if (status === "error") {
          clearClaimsGenerationState(chapterId);
          stopPolling(chapterId);
          toast.error("Claims generation failed");
        } else if (status === "cancelled") {
          clearClaimsGenerationState(chapterId);
          stopPolling(chapterId);
        }
      } catch (error) {
        clearClaimsGenerationState(chapterId);
        stopPolling(chapterId);
        toast.error("Error checking generation status");
      }
    };

    await poll();
  },

  stopPolling: (chapterId) => {
    set((state) => {
      if (state.pollingTimeouts[chapterId]) {
        clearTimeout(state.pollingTimeouts[chapterId]);
      }
      const newTimeouts = { ...state.pollingTimeouts };
      delete newTimeouts[chapterId];
      return { pollingTimeouts: newTimeouts };
    });
  },

  cancelGeneration: async (chapterId: string) => {
    const { clearClaimsGenerationState, stopPolling, claimsGenerationStates, setClaimsGenerationState } = get();

    const generationId = claimsGenerationStates[chapterId]?.generationId;
    if (!generationId) return;

    setClaimsGenerationState(chapterId, { isCancelling: true });

    await postCancelGeneration(Number(generationId))
      .then(() => {
        clearClaimsGenerationState(chapterId);
        stopPolling(chapterId);
        toast.info("Claims generation cancelled");
      })
      .catch((error) => {
        setClaimsGenerationState(chapterId, { isCancelling: false });
        toast.error("Failed to cancel claims generation");
        console.error(error);
      });
  },

  clearAllPolling: () => {
    const { pollingTimeouts } = get();
    Object.values(pollingTimeouts).forEach((timeout) => clearTimeout(timeout));
    set({ pollingTimeouts: {} });
  }
}));

export const selectIsGenerating = (chapterId: string | undefined) => (state: TClaimsStore) => {
  if (!chapterId) return false;
  const { claimsGenerationStates } = state;
  const isGenerating = claimsGenerationStates[chapterId]?.isGenerating ?? false;
  return isGenerating;
};

export const selectIsCancelling = (chapterId: string | undefined) => (state: TClaimsStore) => {
  if (!chapterId) return false;
  return state.claimsGenerationStates[chapterId]?.isCancelling ?? false;
};

export const selectIsInitialized = (chapterId: string | undefined) => (state: TClaimsStore) => {
  if (!chapterId) return false;
  return state.initializedChapters.has(chapterId);
};

export const selectProgressMessage = (chapterId: string | undefined) => (state: TClaimsStore) => {
  if (!chapterId) return "";
  return state.claimsGenerationStates[chapterId]?.progressMessage ?? "";
};

export const selectGenerationId = (chapterId: string | undefined) => (state: TClaimsStore) => {
  if (!chapterId) return null;
  return state.claimsGenerationStates[chapterId]?.generationId ?? null;
};

export const startGeneration = (projectId: number, chapterId: number, onComplete: () => Promise<void>) => {
  useClaimsStoreInternal.getState().startGeneration(projectId, chapterId, onComplete);
};

export const cancelGeneration = (chapterId: string) => {
  return useClaimsStoreInternal.getState().cancelGeneration(chapterId);
};

export const stopPolling = (chapterId: string) => {
  useClaimsStoreInternal.getState().stopPolling(chapterId);
};

export const initializePolling = (projectId: string, chapterId: string, onComplete: () => Promise<void>) => {
  useClaimsStoreInternal.getState().initializePolling(projectId, chapterId, onComplete);
};

export function useInitializeClaimsPolling({
  projectId,
  chapterId,
  onComplete
}: {
  projectId: string;
  chapterId: string | undefined;
  onComplete: () => Promise<void>;
}) {
  useEffect(() => {
    if (!chapterId || !projectId) return;
    initializePolling(projectId, chapterId, onComplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, chapterId]);
}

export const useClaimsStoreSelector = <T>(selector: (state: TClaimsStore) => T): T => useClaimsStoreInternal(selector);
